import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useCartSync } from "@/hooks/useCartSync";
import Index from "./pages/Index.tsx";
import ScrollToTop from "@/components/ScrollToTop";

// Lazy-load non-critical routes to reduce the initial bundle size.
// Only the home (Index) is included in the main chunk; everything else
// is fetched on-demand when the user navigates there.
const ProductDetail = lazy(() => import("./pages/ProductDetail.tsx"));
const MerchDigital = lazy(() => import("./pages/MerchDigital.tsx"));
const HechoAMano = lazy(() => import("./pages/HechoAMano.tsx"));
const PedirRetrato = lazy(() => import("./pages/PedirRetrato.tsx"));
const Admin = lazy(() => import("./pages/Admin.tsx"));
const Faq = lazy(() => import("./pages/Faq.tsx"));
const AvisoPrivacidad = lazy(() => import("./pages/AvisoPrivacidad.tsx"));
const Terminos = lazy(() => import("./pages/Terminos.tsx"));
const Cookies = lazy(() => import("./pages/Cookies.tsx"));
const Blog = lazy(() => import("./pages/Blog.tsx"));
const BlogPost = lazy(() => import("./pages/BlogPost.tsx"));
const Casting = lazy(() => import("./pages/Casting.tsx"));
const Bazar = lazy(() => import("./pages/Bazar.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));

// Defer non-critical UI widgets so they don't bloat the initial bundle.
const ChatBot = lazy(() =>
  import("@/components/ChatBot").then((m) => ({ default: m.ChatBot }))
);
const CookieBanner = lazy(() => import("@/components/CookieBanner"));
const PromoBanner = lazy(() => import("@/components/PromoBanner"));
const WhatsAppFloat = lazy(() => import("@/components/WhatsAppFloat"));
const EmailCapturePopup = lazy(() => import("@/components/EmailCapturePopup"));

const queryClient = new QueryClient();

const AppContent = () => {
  useCartSync();
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/product/:handle" element={<ProductDetail />} />
          <Route path="/merch-digital" element={<MerchDigital />} />
          <Route path="/hecho-a-mano" element={<HechoAMano />} />
          <Route path="/pedir-retrato" element={<PedirRetrato />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/aviso-privacidad" element={<AvisoPrivacidad />} />
          <Route path="/terminos" element={<Terminos />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/casting" element={<Casting />} />
          <Route path="/bazar" element={<Bazar />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      <Suspense fallback={null}>
        {/* ChatBot, PromoBanner y EmailCapturePopup desactivados */}
        <WhatsAppFloat />
        <CookieBanner />
      </Suspense>
    </BrowserRouter>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <AppContent />
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
